import React from 'react';
import DataCard from '../common/DataCard';
import Loader from '../common/Loader';
import Pagination from '../common/Pagination';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import IconButton from '../common/IconButton';
import { MessageCircleWarning, NotebookPen, Pen, Trash, Target, Award, FileText } from 'lucide-react';
import { ScoreDTO } from '../../services/apiClient';
import Badge from '../common/Badge';
import { getBadgeColors } from '../../utils/displayUtils';
import { ScoreStatusConstants } from '../../constants/ScoreStatusConstants';
import { FirearmCaliberConstants } from '../../constants/FirearmCaliberConstants';
import { FirearmMakeConstants } from '../../constants/FirearmMakeConstants';
import { useFeedbackDrawer } from '../../context/FeedbackDrawerContext';
import { useScoreApi } from '../../hooks/useScoreApi';

interface ScoreDataCardProps {
    data: ScoreDTO[];
    handleEdit: (row: ScoreDTO) => void;
    handleDelete: (row: ScoreDTO) => void;
    currentPage: number;
    totalPages: number;
    setCurrentPage: (page: number) => void;
    pageSize: number;
    setPageSize: (size: number) => void;
    isLoading: boolean;

    // Optional action renderers injected by the List page
    renderHeaderActions?: (row: ScoreDTO) => React.ReactNode;
    renderPrimaryActions?: (row: ScoreDTO) => React.ReactNode;
    renderSecondaryActions?: (row: ScoreDTO) => React.ReactNode;
}

const ScoreDataCard: React.FC<ScoreDataCardProps> = ({
    data,
    handleEdit,
    handleDelete,
    currentPage,
    totalPages,
    setCurrentPage,
    pageSize,
    setPageSize,
    isLoading,
    renderHeaderActions,
    renderPrimaryActions,
    renderSecondaryActions,
}) => {
    const { showFeedback } = useFeedbackDrawer();
    const { getFeedback } = useScoreApi();

    const shouldShowEditDeleteActions = (row: ScoreDTO): boolean => {
        return row.scoreStatusId !== ScoreStatusConstants.APPROVED &&
            row.scoreStatusId !== ScoreStatusConstants.APPROVED_WITH_FEEDBACK &&
            row.scoreStatusId !== ScoreStatusConstants.OUTSTANDING &&
            row.scoreStatusId !== ScoreStatusConstants.DECLINED;
    };

    return (
        <div>
            {isLoading ? (
                <div className="py-8 flex justify-center"><Loader /></div>
            ) : data.length === 0 ? (
                <div className="py-8 text-center text-sm text-gray-500">No records found</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {data.map((row) => {
                        const leftBadge = (row: ScoreDTO) => {
                            const base = row.scoreStatus?.colourGrading?.colour ?? '#ffffffff';
                            const { bg, text } = getBadgeColors(base, 0.18, 0.40);
                            const title = row.scoreStatus?.title || 'Unknown';

                            return base ? (
                                <Badge
                                    label={title}
                                    variant="custom"
                                    customBgColor={bg}
                                    customTextColor={text}
                                />
                            ) : (
                                <span>{row.scoreStatus?.title || 'Unknown'}</span>
                            );
                        };

                        const title = row.exerciseType?.title ?? 'N/A';

                        // Calculate percentage display
                        const getScoreDisplay = (score: ScoreDTO) => {
                            if (score.grandTotalPercentage !== undefined && score.grandTotalPercentage !== null) {
                                return `${score.grandTotalPercentage}%`;
                            } else if (score.grandTotalScore !== undefined && score.grandTotalScore !== null) {
                                return `${score.grandTotalScore}`;
                            } else {
                                return 'N/A';
                            }
                        };

                        return (
                            <DataCard<ScoreDTO>
                                key={row.id}
                                item={row}
                                collapsibleOnMobile={true}
                                leftBadgeNode={() => leftBadge(row)}
                                rightMeta={() => {
                                    return {
                                        label: 'Submitted',
                                        value:
                                            <span>
                                                {row.createdUtc ? convertToUserTime(String(row.createdUtc), true) : 'n/a'}
                                                {row.complianceYearDisplay ? ` [for ${row.complianceYearDisplay}]` : ''}
                                            </span>,
                                    };
                                }}
                                title={() => title}
                                media={() => (
                                    <div className="text-sm text-slate-600 -mt-2 mb-2">
                                        {row.scoreType?.title ?? ''}
                                    </div>
                                )}
                                headerActions={renderHeaderActions}
                                fields={[
                                    {
                                        label: 'Activity Date',
                                        render: (row) => row.dateActivity ? convertToUserTime(String(row.dateActivity), true) : 'N/A'
                                    },
                                    {
                                        label: 'Shooting Range Name',
                                        render: (row) => row.location ?? 'N/A'
                                    },
                                    {
                                        label: 'Specific Exercise',
                                        render: (row) => row.exerciseSpecific?.title ?? 'N/A'
                                    },
                                    {
                                        label: 'Firearm',
                                        hideIf: (row) => !row.memberFirearm,
                                        render: (row) => [
                                            row.memberFirearm?.firearmMakeId === FirearmMakeConstants.NOT_ON_LIST ? row.memberFirearm?.firearmMakeOther : row.memberFirearm?.firearmMake?.title ?? 'Unknown',
                                            row.memberFirearm?.firearmCaliberId === FirearmCaliberConstants.NOT_ON_LIST ? row.memberFirearm?.caliberOther : row.memberFirearm?.caliber?.title ?? 'Unknown'
                                        ].filter(Boolean).join(', ') || 'N/A'
                                    },
                                    {
                                        label: 'Mark / Score',
                                        render: (row) => `${row.grandTotalScore} / ${row.grandTotalPercentage}%`
                                    },
                                ]}
                                files={[
                                    ...(row.scoreTargetFiles?.map((file, index) => ({
                                        hideIf: () => false,
                                        label: `Document (${index + 1})`,
                                        id: file.fileRecordId || ''
                                    })) || []),
                                    {
                                        hideIf: () => !row.fileAdditionalId,
                                        label: 'Additional Document',
                                        id: row.fileAdditionalId || ''
                                    },
                                ]}
                                primaryActions={
                                    renderPrimaryActions ??
                                    ((row) => (
                                        <div className="flex justify-between w-full">
                                            <div className="flex gap-3">
                                                {row.feedbackFileId && (
                                                    <IconButton
                                                        label='Feedback'
                                                        color="blue"
                                                        icon={<MessageCircleWarning />}
                                                        onClick={() => showFeedback(() => getFeedback(row.id!))}
                                                    />
                                                )}
                                                {row.notes && (
                                                    <IconButton
                                                        label='Notes'
                                                        content={row.notes}
                                                        color="blue"
                                                        icon={<NotebookPen />}
                                                    />
                                                )}
                                                {row.scoreSections && row.scoreSections.length > 0 && (
                                                    <IconButton
                                                        label='Score Sections'
                                                        content={`${row.scoreSections.length} section(s)`}
                                                        color="green"
                                                        icon={<Award />}
                                                    />
                                                )}
                                            </div>
                                            <div className="flex gap-3">
                                                {shouldShowEditDeleteActions(row) && (
                                                    <>
                                                        <IconButton
                                                            label='Edit'
                                                            color="orange"
                                                            icon={<Pen />}
                                                            onClick={() => handleEdit(row)}
                                                        />
                                                        <IconButton
                                                            label='Delete'
                                                            color="red"
                                                            icon={<Trash />}
                                                            onClick={() => handleDelete(row)}
                                                        />
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                }
                                secondaryActions={renderSecondaryActions}
                            />
                        );
                    })}
                </div>
            )}

            {!isLoading && data.length > 0 && (
                <div className="mt-4">
                    <Pagination
                        currentPage={Number.isFinite(currentPage) && currentPage > 0 ? currentPage : 1}
                        totalPages={Number.isFinite(totalPages) && totalPages > 0 ? totalPages : 1}
                        onPageChange={setCurrentPage}
                        pageSize={Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 12}
                        onPageSizeChange={setPageSize}
                    />
                </div>
            )}
        </div>
    );
};

export default ScoreDataCard;